// src/store/transfer.slice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { transferService } from "../services/transfer.service";

// Thunks

export const fetchTransfers = createAsyncThunk(
  "transfer/fetchTransfers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await transferService.getAllTransfers();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts");
    }
  }
);

export const fetchActiveTransfers = createAsyncThunk(
  "transfer/fetchActiveTransfers",
  async (_, { rejectWithValue }) => {
    try {
      return await transferService.getActiveTransfers();
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts actifs");
    }
  }
);

export const fetchInactiveTransfers = createAsyncThunk(
  "transfer/fetchInactiveTransfers",
  async (_, { rejectWithValue }) => {
    try {
      return await transferService.getInactiveTransfers();
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts inactifs");
    }
  }
);

export const fetchTransferById = createAsyncThunk(
  "transfer/fetchTransferById",
  async (id, { rejectWithValue }) => {
    try {
      return await transferService.getTransferById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération du transfert");
    }
  }
);

export const fetchTransfersByFromShop = createAsyncThunk(
  "transfer/fetchTransfersByFromShop",
  async (shopId, { rejectWithValue }) => {
    try {
      return await transferService.getTransfersByFromShop(shopId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts (from shop)");
    }
  }
);

export const fetchTransfersByToShop = createAsyncThunk(
  "transfer/fetchTransfersByToShop",
  async (shopId, { rejectWithValue }) => {
    try {
      return await transferService.getTransfersByToShop(shopId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts (to shop)");
    }
  }
);

export const fetchTransfersByStatus = createAsyncThunk(
  "transfer/fetchTransfersByStatus",
  async (statusId, { rejectWithValue }) => {
    try {
      return await transferService.getTransfersByStatus(statusId);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts par statut");
    }
  }
);

export const fetchTransfersByDateRange = createAsyncThunk(
  "transfer/fetchTransfersByDateRange",
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      return await transferService.getTransfersByDateRange(startDate, endDate);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la récupération des transferts par date");
    }
  }
);

export const addTransfer = createAsyncThunk(
  "transfer/addTransfer",
  async (transfer, { rejectWithValue }) => {
    try {
      const result = await transferService.createTransfer(transfer);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la création du transfert");
    }
  }
);

export const editTransfer = createAsyncThunk(
  "transfer/editTransfer",
  async ({ id, transferData }, { rejectWithValue }) => {
    try {
      const result = await transferService.updateTransfer(id, transferData);
      return result;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la mise à jour du transfert");
    }
  }
);

export const removeTransfer = createAsyncThunk(
  "transfer/removeTransfer",
  async (id, { rejectWithValue }) => {
    try {
      await transferService.deleteTransfer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la suppression du transfert");
    }
  }
);

export const receiveTransferThunk = createAsyncThunk(
  "transfer/receiveTransfer",
  async ({ id, transferData }, { rejectWithValue }) => {
    try {
      await transferService.receiveTransfer(id, transferData);
      // Retourne l'ID pour pouvoir le retrouver après
      return { id, transferData };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la réception du transfert");
    }
  }
);

// Cancel transfer
export const cancelTransferThunk = createAsyncThunk(
  "transfer/cancelTransfer",
  async (id, { rejectWithValue }) => {
    try {
      await transferService.cancelTransfer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de l'annulation du transfert");
    }
  }
);



export const hardRemoveTransfer = createAsyncThunk(
  "transfer/hardRemoveTransfer",
  async (id, { rejectWithValue }) => {
    try {
      await transferService.hardDeleteTransfer(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la suppression définitive du transfert");
    }
  }
);

// Exemple de search côté front (optionnel)
export const searchTransfers = createAsyncThunk(
  "transfer/searchTransfers",
  async (predicate, { rejectWithValue }) => {
    try {
      return await transferService.searchTransfers(predicate);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Erreur lors de la recherche de transferts");
    }
  }
);

// State initial
const initialState = {
  transfers: [],
  totalCount: 0,
  searchResults: [],
  selectedTransfer: null,
  loading: false,
  searchLoading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: "",
};

// Slice
const transferSlice = createSlice({
  name: "transfer",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedTransfer: (state, action) => {
      state.selectedTransfer = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransfers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Active
      .addCase(fetchActiveTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveTransfers.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchActiveTransfers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Inactive
      .addCase(fetchInactiveTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveTransfers.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchInactiveTransfers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // By id
      .addCase(fetchTransferById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTransfer = action.payload;
      })
      .addCase(fetchTransferById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // From shop
      .addCase(fetchTransfersByFromShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfersByFromShop.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransfersByFromShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // To shop
      .addCase(fetchTransfersByToShop.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfersByToShop.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransfersByToShop.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // By status
      .addCase(fetchTransfersByStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfersByStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransfersByStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // By date range
      .addCase(fetchTransfersByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfersByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
        state.totalCount =
          action.payload?.totalCount ||
          (Array.isArray(action.payload) ? action.payload.length : 0);
      })
      .addCase(fetchTransfersByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add
      .addCase(addTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers.push(action.payload);
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(addTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit
      .addCase(editTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = state.transfers.map((t) =>
          t.transferId === action.payload.transferId ? action.payload : t
        );
        state.showModal = false;
      })
      .addCase(editTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Soft delete
      .addCase(removeTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = state.transfers.filter(
          (t) => t.transferId !== action.payload
        );
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(removeTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Hard delete
      .addCase(hardRemoveTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardRemoveTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers = state.transfers.filter(
          (t) => t.transferId !== action.payload
        );
        state.totalCount -= 1;
        state.showDeleteModal = false;
      })
      .addCase(hardRemoveTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel transfer
      .addCase(cancelTransferThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelTransferThunk.fulfilled, (state, action) => {
        state.loading = false;
        const transferId = action.payload;

        // Retire le transfert annulé de la liste
        state.transfers = state.transfers.filter(
          (t) => t.transferId !== transferId
        );

        if (state.selectedTransfer?.transferId === transferId) {
          state.selectedTransfer = null;
        }

        state.showDeleteModal = false;
        state.showModal = false;
      })
      .addCase(cancelTransferThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search (front)
      .addCase(searchTransfers.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchTransfers.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = Array.isArray(action.payload)
          ? action.payload
          : (action.payload?.items || []);
      })
      .addCase(searchTransfers.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })
      // Receive transfer
      .addCase(receiveTransferThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(receiveTransferThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { id, transferData } = action.payload;

        // Met à jour le transfert dans la liste avec les nouvelles données
        state.transfers = state.transfers.map((t) =>
          t.transferId === id
            ? { ...t, ...transferData } // Fusionne avec les nouvelles données de réception
            : t
        );

        // Met à jour aussi le transfert sélectionné s'il existe
        if (state.selectedTransfer?.transferId === id) {
          state.selectedTransfer = {
            ...state.selectedTransfer,
            ...transferData
          };
        }
      })
      .addCase(receiveTransferThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })



      ;
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedTransfer,
  setSearchQuery,
  clearSearchResults,
} = transferSlice.actions;

export default transferSlice.reducer;
